import { Controller } from 'react-hook-form';
import { FC } from 'react';
import { Popover } from '@headlessui/react';
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg';
import { Option } from '../../../../MainSection/PractitionerProfile/MultipleSelect/MultipleSelect';
import { ReactComponent as DropdownIcon } from 'assets/icons/drop-down.svg';
import ServicesByOperatories from './ServicesByOperatories/ServicesByOperatories';
import { ServicesOperatories } from '../PractitionerSchedule';
import { sortBy } from 'lodash';
import { SERVICE_NAME } from 'utils/constants';

interface PractitionerServicesOperatoriesProps {
  services: Option[];
  operatories: Option[];
  control: any;
}

const PractitionerServicesOperatories: FC<
  PractitionerServicesOperatoriesProps
> = ({ control, services, operatories }) => {
  return (
    <Controller
      control={control}
      name="servicesOperatories"
      rules={{
        validate: (value) => {
          const servicesOperatories = value as ServicesOperatories[];

          const isSomeServicesWithoutOperatories = servicesOperatories.some(
            (item) => item.operatoryIds.length === 0
          );

          if (servicesOperatories.length === 0) {
            return 'Please select at least one service and operatory.';
          }

          if (isSomeServicesWithoutOperatories) {
            return 'Please select at least one operatory for each service.';
          }

          return true;
        },
      }}
      render={({ field, fieldState: { error } }) => {
        const { onChange, value } = field;

        const examCleaning = value.find(
          (item: ServicesOperatories) =>
            item.service.name === SERVICE_NAME.EXAM_CLEANING
        );

        let updatedServices = services;

        if (examCleaning) {
          // REMOVE CHILD SERVICES
          updatedServices = updatedServices.filter(
            (service) => !service.label.includes(SERVICE_NAME.EXAM_CLEANING)
          );

          updatedServices = [
            ...updatedServices,
            {
              label: SERVICE_NAME.EXAM_CLEANING,
              value: examCleaning.service.id,
            },
          ];
        }

        const totalOperatories = operatories.length * updatedServices.length;

        const numberOfSelectedOperatories = (value as ServicesOperatories[])
          .map((item) => item.operatoryIds.length)
          .reduce((a, b) => a + b, 0);

        return (
          <>
            <Popover className="relative">
              <Popover.Button
                disabled={services.length === 0 || operatories.length === 0}
                className={`w-full flex items-center h-4 py-1.3 shadow-primary rounded-[1rem] pl-1 pr-0.5 justify-between ${
                  numberOfSelectedOperatories === 0 ? 'text-grey' : ''
                } ${
                  error ? 'border-secondary-red border-[0.5px]' : ''
                } disabled:cursor-not-allowed`}
              >
                {({ open }) => (
                  <>
                    <span>
                      {numberOfSelectedOperatories === 0
                        ? 'Select Services by Operatories'
                        : `${numberOfSelectedOperatories} of ${totalOperatories} selected`}
                    </span>
                    <DropdownIcon
                      className={`${
                        open ? 'fill-[#666]' : 'fill-[#ccc]'
                      } transition-[fill]`}
                    />
                  </>
                )}
              </Popover.Button>

              <Popover.Panel className="absolute w-full flex flex-row rounded-[1rem] shadow-primary bg-white z-50 h-[32rem] overflow-auto scrollbar text-14 leading-[2.1rem]">
                <ServicesByOperatories
                  services={sortBy(updatedServices, 'label')}
                  operatories={sortBy(operatories, 'label')}
                  value={value}
                  onChange={onChange}
                />
              </Popover.Panel>
            </Popover>
            {error?.message && (
              <div className={`flex items-center gap-x-1 -mt-0.2`}>
                <WarningIcon />
                <span className="text-12 text-red-01">{error.message}</span>
              </div>
            )}
          </>
        );
      }}
    />
  );
};

export default PractitionerServicesOperatories;
